/**
 * Seatics' "How many tickets?" sheet often pushState's so Back can close it.
 * After we SPA-navigate to /checkout, ListingPage unmounts and MapAndLayout
 * closes that sheet with history.back() — which pops checkout (or the extra
 * CheckoutPage back-guard entry) and dumps the shopper on the listing.
 *
 * Suppress native back for a short window around that navigation, and let
 * CheckoutPage ignore those synthetic pops.
 */

const SUPPRESS_MS = 1500;

let suppressBackUntil = 0;
let absorbPopsUntil = 0;
let patched = false;
let originalBack = null;
let originalGo = null;

const now = () => Date.now();

const shouldSuppressNativeBack = () => now() < suppressBackUntil;

const installHistoryBackPatch = () => {
  if (patched || typeof window === 'undefined' || !window.history) return;
  originalBack = window.history.back;
  originalGo = window.history.go;
  window.history.back = function patchedHistoryBack() {
    if (shouldSuppressNativeBack()) return;
    return originalBack.apply(this, arguments);
  };
  window.history.go = function patchedHistoryGo(delta) {
    if (typeof delta === 'number' && delta < 0 && shouldSuppressNativeBack()) return;
    return originalGo.apply(this, arguments);
  };
  patched = true;
};

export const markSeaticsCheckoutNavigation = (ms = SUPPRESS_MS) => {
  const until = now() + Math.max(0, Number(ms) || SUPPRESS_MS);
  suppressBackUntil = until;
  absorbPopsUntil = until;
  installHistoryBackPatch();
};

export const shouldAbsorbSeaticsCheckoutPop = (clock = now()) => clock < absorbPopsUntil;

/** Test helper */
export const resetSeaticsCheckoutHistoryForTests = () => {
  suppressBackUntil = 0;
  absorbPopsUntil = 0;
  if (patched && typeof window !== 'undefined' && window.history) {
    if (originalBack) window.history.back = originalBack;
    if (originalGo) window.history.go = originalGo;
  }
  patched = false;
  originalBack = null;
  originalGo = null;
};

/**
 * Run `fn` with history.back / history.go(-n) no-ops (Seatics MapAndLayout teardown).
 */
export const withoutHistoryBack = fn => {
  if (typeof fn !== 'function') return undefined;
  if (typeof window === 'undefined' || !window.history) {
    return fn();
  }
  const hist = window.history;
  const back = hist.back;
  const go = hist.go;
  hist.back = () => {};
  hist.go = delta => {
    if (typeof delta === 'number' && delta < 0) return undefined;
    return go.call(hist, delta);
  };
  try {
    return fn();
  } finally {
    hist.back = back;
    hist.go = go;
  }
};
